import React, { useEffect, useState } from 'react';

interface CaptureOverlayProps {
  captureData: {
    type: 'photo' | 'video';
    url: string;
    timestamp: string;
  };
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
}

type AIAction = 'enlarge2x' | 'enlarge4x' | 'styleAdjust' | 'fixErrors' | null;

export const CaptureOverlay: React.FC<CaptureOverlayProps> = ({
  captureData,
  onClose,
  onSave,
  onDelete
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeAction, setActiveAction] = useState<AIAction>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseAttempt();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Sie haben ungespeicherte Änderungen. Möchten Sie wirklich schließen?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCloseAttempt();
    }
  };

  const handleAIAction = async (action: AIAction) => {
    if (isProcessing) return;

    setActiveAction(action);
    setIsProcessing(true);
    setHasUnsavedChanges(true);

    // Simulate AI processing (2-3 seconds)
    await new Promise(resolve => setTimeout(resolve, 2500));

    setIsProcessing(false);
    setActiveAction(null);
  };

  const handleSave = () => {
    onSave();
    // Download the image
    const link = document.createElement('a');
    link.href = captureData.url;
    link.download = `capture-${Date.now()}.${captureData.type === 'photo' ? 'png' : 'mp4'}`;
    document.body.appendChild(link);
    link.click();
    try {
      document.body.removeChild(link);
    } catch {
      // Element may already be removed
    }
    onClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete();
    onClose();
  };

  return (
    <div className="capture-overlay" onClick={handleBackdropClick}>
      <div className="capture-overlay__container">
        {/* Header */}
        <div className="capture-overlay__header">
          <h2 className="capture-overlay__title">{captureData.timestamp}</h2>
          <button
            className="capture-overlay__close"
            onClick={handleCloseAttempt}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        {/* Main Content */}
        <div className="capture-overlay__content">
          {/* Preview Area */}
          <div className="capture-overlay__preview">
            {captureData.type === 'photo' ? (
              <img
                src={captureData.url}
                alt="Capture preview"
                className="capture-overlay__image"
              />
            ) : (
              <video
                src={captureData.url}
                controls
                className="capture-overlay__video"
              />
            )}
          </div>

          {/* Plaza AI Editor Flyout */}
          <div className="capture-overlay__editor">
            <h3 className="capture-overlay__editor-title">Plaza AI Editor</h3>

            <div className="capture-overlay__editor-buttons">
              <button
                className={`capture-overlay__ai-button ${isProcessing && activeAction === 'enlarge2x' ? 'processing' : ''}`}
                onClick={() => handleAIAction('enlarge2x')}
                disabled={isProcessing}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"/>
                  <polyline points="9 21 3 21 3 15"/>
                  <line x1="21" y1="3" x2="14" y2="10"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
                <span>Vergrößern 2X</span>
                {isProcessing && activeAction === 'enlarge2x' && (
                  <span className="capture-overlay__spinner"></span>
                )}
              </button>

              <button
                className={`capture-overlay__ai-button ${isProcessing && activeAction === 'enlarge4x' ? 'processing' : ''}`}
                onClick={() => handleAIAction('enlarge4x')}
                disabled={isProcessing}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"/>
                  <polyline points="9 21 3 21 3 15"/>
                  <line x1="21" y1="3" x2="14" y2="10"/>
                  <line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
                <span>Vergrößern 4X</span>
                {isProcessing && activeAction === 'enlarge4x' && (
                  <span className="capture-overlay__spinner"></span>
                )}
              </button>

              <button
                className={`capture-overlay__ai-button ${isProcessing && activeAction === 'styleAdjust' ? 'processing' : ''}`}
                onClick={() => handleAIAction('styleAdjust')}
                disabled={isProcessing}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v6m0 0v6m0-6h6m-6 0H6"/>
                  <circle cx="12" cy="12" r="10"/>
                </svg>
                <span>Stil Anpassen</span>
                {isProcessing && activeAction === 'styleAdjust' && (
                  <span className="capture-overlay__spinner"></span>
                )}
              </button>

              <button
                className={`capture-overlay__ai-button ${isProcessing && activeAction === 'fixErrors' ? 'processing' : ''}`}
                onClick={() => handleAIAction('fixErrors')}
                disabled={isProcessing}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span>Fehler Beheben</span>
                {isProcessing && activeAction === 'fixErrors' && (
                  <span className="capture-overlay__spinner"></span>
                )}
              </button>
            </div>

            {isProcessing && (
              <div className="capture-overlay__processing-message">
                AI-Verarbeitung läuft...
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="capture-overlay__footer">
          <button
            className="capture-overlay__action-button capture-overlay__action-button--delete"
            onClick={handleDelete}
          >
            Löschen
          </button>
          <button
            className="capture-overlay__action-button capture-overlay__action-button--save"
            onClick={handleSave}
          >
            Speichern
          </button>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="capture-overlay__confirm-dialog">
            <div className="capture-overlay__confirm-content">
              <h3>Aufnahme löschen?</h3>
              <p>Möchten Sie diese Aufnahme wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.</p>
              <div className="capture-overlay__confirm-buttons">
                <button
                  className="capture-overlay__confirm-button capture-overlay__confirm-button--cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Abbrechen
                </button>
                <button
                  className="capture-overlay__confirm-button capture-overlay__confirm-button--confirm"
                  onClick={confirmDelete}
                >
                  Löschen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
